<template>
    <div class="space-y-4">
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-16">
            <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" />
            <p class="mt-3">{{ t('availability.loading') }}</p>
        </div>

        <!-- Content -->
        <div v-else class="space-y-4">
            <!-- Quick Actions Bar -->
            <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 p-2 rounded-lg bg-surface-50 border border-surface-200">
                <div class="flex items-center gap-2">
                    <i class="fa-solid fa-bolt text-primary"></i>
                    <span class="font-semibold">{{ t('availability.quickActions') }}</span>
                </div>
                <div class="flex flex-wrap gap-2">
                    <Button :label="t('availability.weekdays')" icon="fa-solid fa-briefcase" @click="$emit('applyWeekdays')" outlined size="small" />
                    <Button :label="t('availability.fullWeek')" icon="fa-regular fa-calendar" @click="$emit('applyFullWeek')" outlined size="small" />
                    <Button :label="t('availability.clearAll')" icon="fa-regular fa-trash-can" @click="$emit('clearAll')" severity="danger" size="small" />
                </div>
            </div>

            <!-- Slots Header -->
            <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                <h5 class="font-semibold">{{ t('availability.configureSlots') }}</h5>
                <Button icon="fa-solid fa-plus" :label="t('availability.addSlot')" @click="$emit('addSlot')" class="w-full sm:w-auto" />
            </div>

            <!-- Empty State -->
            <div v-if="slots.length === 0 && slotsPendingDelete.length === 0" class="text-center py-12 rounded-lg border-2 border-dashed border-surface-300 bg-surface-50">
                <i class="fa-regular fa-calendar-xmark text-5xl text-surface-400 mb-3"></i>
                <p class="font-semibold mb-1">{{ t('availability.noSlots') }}</p>
                <p>{{ t('availability.noSlotsDescription') }}</p>
            </div>

            <!-- Pending Deletions List -->
            <div v-if="slotsPendingDelete.length > 0" class="space-y-2">
                <h5 class="text-sm font-semibold text-red-600 flex items-center gap-2">
                    <i class="fa-solid fa-trash-can"></i>
                    {{ t('availability.pendingDeletions') }}
                </h5>
                <div v-for="pending in slotsPendingDelete" :key="pending.slot.id" class="rounded-lg border-2 border-red-200 bg-red-50 p-3">
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div class="flex items-start gap-3 min-w-0">
                            <i class="fa-solid fa-trash-can text-red-500 mt-1 shrink-0"></i>
                            <div class="min-w-0">
                                <p class="font-medium text-red-800 text-sm">
                                    <span class="truncate">{{ pending.slot.title || getDayLabel(pending.slot.dayOfWeek) }}</span>
                                    <span class="text-red-600"> ({{ pending.slot.start }} - {{ pending.slot.end }}) </span>
                                </p>
                                <p class="text-red-600 text-sm">
                                    {{ pending.hasReservations ? t('availability.deleteWithReservations') : t('availability.deleteNoReservations') }}
                                </p>
                                <p v-if="pending.reason" class="text-red-500 mt-1 italic text-sm">{{ t('availability.reason') }}: {{ pending.reason }}</p>
                            </div>
                        </div>
                        <Button icon="fa-solid fa-undo" :label="t('availability.undoDelete')" @click="$emit('cancelSlotDeletion', pending.slot.id!)" size="small" severity="secondary" outlined class="w-full sm:w-auto shrink-0" />
                    </div>
                </div>
            </div>

            <!-- Slots List -->
            <div v-if="slots.length > 0" class="space-y-3">
                <div
                    v-for="(slot, index) in slots"
                    :key="`slot-${slot.id || index}`"
                    class="group relative rounded-lg border bg-white hover:shadow-md transition-all duration-200"
                    :class="slot.validFrom || slot.validUntil ? 'border-orange-200 hover:border-orange-300 bg-orange-50/30' : 'border-surface-200 hover:border-primary-200'"
                >
                    <div class="p-3 pt-2 space-y-3">
                        <!-- Badge for slot type -->
                        <div class="flex items-center justify-between">
                            <Tag v-if="slot.validFrom || slot.validUntil" :value="t('availability.slotType.custom')" severity="warning" icon="fa-solid fa-calendar-days" />
                            <Tag v-else :value="t('availability.slotType.standard')" severity="info" icon="fa-regular fa-calendar" />
                            <Button
                                icon="fa-regular fa-trash-can"
                                @click="$emit('requestRemoveSlot', slot, index)"
                                text
                                rounded
                                severity="danger"
                                :loading="checkingDeletable === slot.id"
                                class="opacity-0 group-hover:opacity-100 transition-opacity"
                            />
                        </div>

                        <!-- Title and Description -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label class="block font-semibold mb-1.5"> {{ t('availability.fields.title') }} <span class="text-red-500">*</span> </label>
                                <InputText :modelValue="slot.title" @update:modelValue="$emit('updateSlot', index, 'title', $event)" :placeholder="t('availability.fields.titlePlaceholder')" class="w-full" />
                            </div>
                            <div>
                                <label class="block font-semibold mb-1.5">
                                    {{ t('availability.fields.description') }}
                                </label>
                                <InputText :modelValue="slot.description" @update:modelValue="$emit('updateSlot', index, 'description', $event)" :placeholder="t('availability.fields.descriptionPlaceholder')" class="w-full" />
                            </div>
                        </div>

                        <!-- Day and Time -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div class="md:col-span-1 col-span-1">
                                <label class="block font-semibold mb-1.5"> {{ t('availability.fields.day') }} <span class="text-red-500">*</span> </label>
                                <Select
                                    :modelValue="slot.dayOfWeek"
                                    @update:modelValue="$emit('updateSlot', index, 'dayOfWeek', $event)"
                                    :options="daysOfWeek"
                                    optionLabel="label"
                                    optionValue="value"
                                    :placeholder="t('availability.fields.selectDay')"
                                    class="w-full"
                                    @change="markTouched(index, 'dayOfWeek')"
                                    @blur="markTouched(index, 'dayOfWeek')"
                                >
                                    <template #value="slotProps">
                                        <div v-if="slotProps.value != null" class="flex items-center gap-2">
                                            <span>{{ getDayLabel(slotProps.value) }}</span>
                                        </div>
                                        <span v-else>{{ slotProps.placeholder }}</span>
                                    </template>
                                    <template #option="slotProps">
                                        <div class="flex items-center gap-2">
                                            <span>{{ slotProps.option.label }}</span>
                                        </div>
                                    </template>
                                </Select>
                                <small v-if="isFieldInvalid(slot, index, 'dayOfWeek')" class="text-red-500 text-xs">
                                    {{ t('availability.validation.dayRequired') }}
                                </small>
                            </div>

                            <div class="md:col-span-1 col-span-1 flex flex-row gap-2 items-end">
                                <div class="flex-1">
                                    <label class="block font-semibold mb-1.5"> {{ t('availability.fields.start') }} <span class="text-red-500">*</span> </label>
                                    <DatePicker
                                        :ref="
                                            (el: any) => {
                                                if (el) timePickerRefs[`start-${index}`] = el;
                                            }
                                        "
                                        timeOnly
                                        :stepMinute="15"
                                        :modelValue="timeStringToDate(slot.start)"
                                        @update:modelValue="
                                            (value) => {
                                                $emit('updateSlot', index, 'start', dateToTimeString(value as Date));
                                                markTouched(index, 'start');
                                            }
                                        "
                                        @hide="
                                            () => {
                                                const rounded = roundToNearest15Minutes(timeStringToDate(slot.start));
                                                $emit('updateSlot', index, 'start', dateToTimeString(rounded));
                                            }
                                        "
                                        @show="
                                            () =>
                                                handleTimePickerShow(`start-${index}`, slot.start, (v) => {
                                                    emit('updateSlot', index, 'start', v);
                                                    markTouched(index, 'start');
                                                })
                                        "
                                        class="w-full"
                                    >
                                        <template #footer>
                                            <div class="flex justify-end pt-2 border-t">
                                                <Button
                                                    :label="t('common.apply')"
                                                    size="small"
                                                    severity="success"
                                                    @click="
                                                        confirmTimePicker(`start-${index}`, (v) => {
                                                            emit('updateSlot', index, 'start', v);
                                                            markTouched(index, 'start');
                                                        })
                                                    "
                                                />
                                            </div>
                                        </template>
                                    </DatePicker>
                                    <small v-if="isFieldInvalid(slot, index, 'start')" class="text-red-500 text-xs">
                                        {{ t('availability.validation.startRequired') }}
                                    </small>
                                </div>
                                <div class="flex-1">
                                    <label class="block font-semibold mb-1.5"> {{ t('availability.fields.end') }} <span class="text-red-500">*</span> </label>
                                    <DatePicker
                                        :ref="
                                            (el: any) => {
                                                if (el) timePickerRefs[`end-${index}`] = el;
                                            }
                                        "
                                        timeOnly
                                        :stepMinute="15"
                                        :modelValue="timeStringToDate(slot.end)"
                                        @update:modelValue="
                                            (value) => {
                                                $emit('updateSlot', index, 'end', dateToTimeString(value as Date));
                                                markTouched(index, 'end');
                                            }
                                        "
                                        @hide="
                                            () => {
                                                const rounded = roundToNearest15Minutes(timeStringToDate(slot.end));
                                                $emit('updateSlot', index, 'end', dateToTimeString(rounded));
                                            }
                                        "
                                        @show="
                                            () =>
                                                handleTimePickerShow(`end-${index}`, slot.end, (v) => {
                                                    emit('updateSlot', index, 'end', v);
                                                    markTouched(index, 'end');
                                                })
                                        "
                                        class="w-full"
                                    >
                                        <template #footer>
                                            <div class="flex justify-end pt-2 border-t">
                                                <Button
                                                    :label="t('common.apply')"
                                                    size="small"
                                                    severity="success"
                                                    @click="
                                                        confirmTimePicker(`end-${index}`, (v) => {
                                                            emit('updateSlot', index, 'end', v);
                                                            markTouched(index, 'end');
                                                        })
                                                    "
                                                />
                                            </div>
                                        </template>
                                    </DatePicker>
                                    <small v-if="isFieldInvalid(slot, index, 'end')" class="text-red-500 text-xs">
                                        {{ t('availability.validation.endRequired') }}
                                    </small>
                                    <small v-else-if="isTimeOrderInvalid(slot)" class="text-red-500 text-xs">
                                        {{ t('availability.validation.endAfterStart') }}
                                    </small>
                                </div>
                            </div>
                        </div>

                        <!-- Date Range -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-surface-200">
                            <div>
                                <label class="block font-semibold mb-1.5">
                                    {{ t('availability.fields.validFrom') }}
                                </label>
                                <DatePicker
                                    :modelValue="getDateFromTimestamp(slot.validFrom)"
                                    @update:modelValue="$emit('updateSlotStartDate', index, $event)"
                                    dateFormat="dd/mm/yy"
                                    :placeholder="t('availability.fields.datePlaceholder')"
                                    class="w-full"
                                    showIcon
                                    showButtonBar
                                />
                            </div>
                            <div>
                                <label class="block font-semibold mb-1.5">
                                    {{ t('availability.fields.validUntil') }}
                                </label>
                                <DatePicker
                                    :modelValue="getDateFromTimestamp(slot.validUntil)"
                                    @update:modelValue="$emit('updateSlotEndDate', index, $event)"
                                    dateFormat="dd/mm/yy"
                                    :placeholder="t('availability.fields.datePlaceholder')"
                                    class="w-full"
                                    showIcon
                                    showButtonBar
                                />
                            </div>
                        </div>

                        <!-- Slot Duration Badge -->
                        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-3 border-t border-surface-200">
                            <span class="text-sm">
                                <i class="fa-regular fa-hourglass mr-1"></i>
                                {{ t('availability.duration') }}: <strong class="text-primary">{{ getSlotDuration(slot) }}h</strong>
                            </span>
                            <span v-if="slot.validFrom || slot.validUntil" class="text-orange-700 text-sm">
                                <i class="fa-solid fa-calendar-days mr-1"></i>
                                <span v-if="slot.validFrom && slot.validUntil"> {{ formatDate(slot.validFrom) }} - {{ formatDate(slot.validUntil) }} </span>
                                <span v-else-if="slot.validFrom">{{ t('availability.from') }} {{ formatDate(slot.validFrom) }}</span>
                                <span v-else>{{ t('availability.until') }} {{ formatDate(slot.validUntil) }}</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { SlotPendingDelete } from '@/composables/useResourceAvailability';
import type { AvailabilitySlot } from '@/lib/types/availability';
import { ceilToNext15Minutes, dateToTimeString, roundToNearest15Minutes, timeStringToDate } from '@/utils/date';
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

// Dynamic refs for time pickers (to allow closing from footer button)
const timePickerRefs = ref<Record<string, any>>({});

/**
 * Read the currently displayed time from a DatePicker's internal state,
 * round to nearest 15 min, apply it via callback, then close the popup.
 */
function confirmTimePicker(key: string, applyValue: (timeStr: string) => void) {
    const picker = timePickerRefs.value[key];
    if (picker) {
        const hour = picker.currentHour ?? 0;
        const minute = picker.currentMinute ?? 0;
        const date = new Date();
        date.setHours(hour, minute, 0, 0);
        const rounded = roundToNearest15Minutes(date);
        applyValue(dateToTimeString(rounded));
        picker.overlayVisible = false;
    }
}

/**
 * When the popup opens and the input is empty, populate with the next 15-min step.
 */
function handleTimePickerShow(key: string, currentValue: string | null | undefined, applyValue: (timeStr: string) => void) {
    if (!currentValue || !currentValue.trim()) {
        const now = new Date();
        const next = ceilToNext15Minutes(now);
        const timeStr = dateToTimeString(next);
        applyValue(timeStr);
    }
}

// Track touched state per slot (by index)
const slotsTouched = reactive<Map<number, { dayOfWeek: boolean; start: boolean; end: boolean }>>(new Map());

// Get or create touched state for a slot
function getSlotTouched(index: number) {
    if (!slotsTouched.has(index)) {
        slotsTouched.set(index, { dayOfWeek: false, start: false, end: false });
    }
    return slotsTouched.get(index)!;
}

// Mark a slot field as touched
function markTouched(index: number, field: 'dayOfWeek' | 'start' | 'end') {
    const touched = getSlotTouched(index);
    touched[field] = true;
}

// Check if a slot field is invalid
function isFieldInvalid(slot: AvailabilitySlot, index: number, field: 'dayOfWeek' | 'start' | 'end'): boolean {
    const touched = slotsTouched.get(index);
    if (!touched || !touched[field]) return false;

    if (field === 'dayOfWeek') {
        return !slot.dayOfWeek;
    }
    if (field === 'start') {
        return !slot.start || slot.start.trim() === '';
    }
    if (field === 'end') {
        return !slot.end || slot.end.trim() === '';
    }
    return false;
}

// Check if start time is before end time
function isTimeOrderInvalid(slot: AvailabilitySlot): boolean {
    if (!slot.start || !slot.end) return false;

    const startParts = slot.start.split(':').map(Number);
    const endParts = slot.end.split(':').map(Number);

    if (startParts.length !== 2 || endParts.length !== 2) return false;

    const startMinutes = startParts[0] * 60 + startParts[1];
    const endMinutes = endParts[0] * 60 + endParts[1];

    return startMinutes >= endMinutes;
}

// Time options - 15 minute intervals
const timeOptions = computed(() => {
    const times: string[] = [];
    for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute++) {
            const h = String(hour).padStart(2, '0');
            const m = String(minute).padStart(2, '0');
            times.push(`${h}:${m}`);
        }
    }
    return times;
});

const filteredStartTimes = ref<string[]>([]);
const filteredEndTimes = ref<string[]>([]);

function onStartTimeFocus() {
    filteredStartTimes.value = timeOptions.value;
}

function onEndTimeFocus() {
    filteredEndTimes.value = timeOptions.value;
}

function searchStartTime(event: { query: string }) {
    const query = event.query.toLowerCase().trim();
    if (!query) {
        filteredStartTimes.value = timeOptions.value;
        return;
    }
    filteredStartTimes.value = timeOptions.value.filter((time) => time.includes(query) || time.replace(':', '').includes(query));
}

function searchEndTime(event: { query: string }) {
    const query = event.query.toLowerCase().trim();
    if (!query) {
        filteredEndTimes.value = timeOptions.value;
        return;
    }
    filteredEndTimes.value = timeOptions.value.filter((time) => time.includes(query) || time.replace(':', '').includes(query));
}

interface Props {
    loading: boolean;
    slots: AvailabilitySlot[];
    totalWeeklyHours: number;
    resourceName: string;
    resourceType: string;
    resourceLocation: string;
    daysOfWeek: Array<{ label: string; value: string }>;
    getDayLabel: (dayOfWeek: string) => string;
    getSlotDuration: (slot: AvailabilitySlot) => number;
    getDateFromTimestamp: (timestamp: number | null) => Date | null;
    formatDate: (timestamp: number | null) => string;
    slotsPendingDelete: SlotPendingDelete[];
    checkingDeletable: string | null;
}

interface Emits {
    (e: 'addSlot'): void;
    (e: 'removeSlot', index: number): void;
    (e: 'requestRemoveSlot', slot: AvailabilitySlot, index: number): void;
    (e: 'cancelSlotDeletion', slotId: string): void;
    (e: 'updateSlot', index: number, field: string, value: any): void;
    (e: 'updateSlotStartDate', index: number, date: Date | Date[] | (Date | null)[] | null | undefined): void;
    (e: 'updateSlotEndDate', index: number, date: Date | Date[] | (Date | null)[] | null | undefined): void;
    (e: 'formatSlotTime', index: number, type: 'start' | 'end'): void;
    (e: 'applyWeekdays'): void;
    (e: 'applyFullWeek'): void;
    (e: 'clearAll'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const { t } = useI18n();

// Get current week date range
const now = new Date();
const currentWeekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
const currentWeekEnd = new Date(currentWeekStart);
currentWeekEnd.setDate(currentWeekEnd.getDate() + 6);
currentWeekEnd.setHours(23, 59, 59, 999);
</script>
